import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Card } from '../shared';
import { getTimeOfDayGreeting, formatRelativeTime } from '../../../utils/dashboardState';
import { ConnectionSummary } from '../../connections';
import AddMinorModal from '../../connections/AddMinorModal';
import { axiosInstance } from '../../../services/api';
import { getFamilies } from '../../../utils/secureStorage';
import WisdomMomentsFeed from '../../videoactivation/WisdomMomentsFeed';
import SpeakerNotificationBanner from '../../videoactivation/SpeakerNotificationBanner';

/**
 * ActiveState Component
 *
 * Full dashboard for users with both connections and stories
 * Shows engagement, questions, stories, and activity
 */
function ActiveState({ data }) {
  const navigate = useNavigate();
  const { user, stats, connections, questions, stories, activities, notificationCount } = data;

  const [isAddMinorModalOpen, setIsAddMinorModalOpen] = useState(false);
  const [isAddingMinor, setIsAddingMinor] = useState(false);

  const greeting = getTimeOfDayGreeting();

  /**
   * Handle adding a minor child
   */
  const handleAddMinor = async (minorData) => {
    setIsAddingMinor(true);
    try {
      // Get user's family ID from localStorage
      const families = getFamilies();
      const familyId = families[0]?.id;

      if (!familyId) {
        throw new Error('No family found. Please create a family first.');
      }

      // Add minor to the user's account with family_id
      await axiosInstance.post('/connections/add-minor/', {
        ...minorData,
        family_id: familyId
      });

      // Close modal and reload page to show new minor
      setIsAddMinorModalOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Error adding minor:', error);
      const errorMessage = error.response?.data?.error || 'Failed to add child. Please try again.';
      alert(errorMessage);
    } finally {
      setIsAddingMinor(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-[#556B2F] to-[#6B8E23] text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">
            {greeting}, {user.firstName}! 👋
          </h1>
          <p className="text-xl opacity-90">
            Your family is waiting to hear from you
          </p>
          <div className="flex items-center gap-6 mt-4">
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
              <span className="text-2xl font-bold">{stats.storyCount}</span>
              <span className="text-sm ml-2 opacity-90">{stats.storyCount === 1 ? 'Story' : 'Stories'} Shared</span>
            </div>
            {stats.milestoneCount > 0 && (
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
                <span className="text-2xl font-bold">{stats.milestoneCount}</span>
                <span className="text-sm ml-2 opacity-90">{stats.milestoneCount === 1 ? 'Milestone' : 'Milestones'}</span>
              </div>
            )}
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
              <span className="text-2xl font-bold">{stats.connectionCount}</span>
              <span className="text-sm ml-2 opacity-90">{stats.connectionCount === 1 ? 'Connection' : 'Connections'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-green-600 font-bold">✓</span>
            <span className="text-gray-700">Adults with own account</span>
          </div>
          <div className="flex items-center gap-2">
            <span>👶</span>
            <span className="text-gray-700">Children (through parent)</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Speaker Notification Banner */}
        <SpeakerNotificationBanner
          onIdentifyClick={(jId) => navigate(`/identify-voices/${jId}`)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Wisdom Moments Feed */}
            <WisdomMomentsFeed />

            {/* Questions Card */}
            {questions && questions.length > 0 && (
              <Card variant="questions">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">❓</span>
                    <h2 className="text-2xl font-bold text-orange-900">
                      Family Questions
                    </h2>
                  </div>
                  <div className="bg-orange-200 text-orange-900 px-4 py-2 rounded-full font-bold">
                    {questions.length} New
                  </div>
                </div>

                <div className="space-y-4">
                  {questions.map(question => (
                    <div
                      key={question.id}
                      className="bg-white rounded-xl p-4 border border-orange-200"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                          style={{ backgroundColor: question.askedBy.avatarColor }}
                        >
                          {question.askedBy.firstName[0]}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800">
                            {question.askedBy.firstName}
                            {question.isFromMinor && question.viaParent && (
                              <span className="text-sm text-blue-600 ml-2">
                                (via {question.viaParent})
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            {question.askedBy.relationship} • Asked {formatRelativeTime(question.askedAt)}
                          </div>
                        </div>
                      </div>
                      <p className="text-lg text-gray-800 mb-3 italic">
                        "{question.text}"
                      </p>
                      <button
                        onClick={() => navigate(`/questions/${question.id}/answer`)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-bold transition-colors"
                      >
                        Answer
                      </button>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Connections Grid */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-3xl">👨‍👩‍👧‍👦</span>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Your Connections
                  </h2>
                </div>
                <button
                  onClick={() => navigate('/connections')}
                  className="text-[#556B2F] font-semibold hover:underline"
                >
                  View All →
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {connections.slice(0, 8).map(connection => (
                  <div
                    key={connection.id}
                    className={`
                      rounded-xl p-3 text-center cursor-pointer transition-all
                      ${connection.hasActivity ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-gray-50 border border-gray-200'}
                      hover:shadow-md hover:transform hover:-translate-y-1
                    `}
                    onClick={() => navigate(`/family-hub/member/${connection.id}`)}
                  >
                    <div className="relative inline-block mb-2">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold"
                        style={{ backgroundColor: connection.avatarColor }}
                      >
                        {connection.firstName[0]}
                      </div>
                      {connection.isOnline && (
                        <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full" />
                      )}
                      {connection.isMinor && (
                        <div className="absolute bottom-0 right-0 text-lg">👶</div>
                      )}
                    </div>
                    <div className="font-semibold text-gray-800 text-sm">
                      {connection.firstName}
                    </div>
                    <div className="text-xs text-gray-600">
                      {connection.relationship}
                    </div>
                    {connection.isMinor && connection.guardianName && (
                      <div className="text-xs text-blue-600 mt-1">
                        via {connection.guardianName}
                      </div>
                    )}
                    {connection.hasActivity && (
                      <div className="text-xs text-yellow-700 font-semibold mt-1">
                        {connection.activityText}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* Stories */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-3xl">📚</span>
                  <h2 className="text-2xl font-bold text-gray-800">
                    Your Stories
                  </h2>
                </div>
                <button
                  onClick={() => navigate('/stories')}
                  className="text-[#556B2F] font-semibold hover:underline"
                >
                  View All →
                </button>
              </div>

              <div className="space-y-4">
                {stories.map(story => (
                  <div
                    key={story.id}
                    className={`
                      rounded-xl p-4 border-2 cursor-pointer transition-all hover:shadow-md
                      ${story.hasNewActivity ? 'bg-yellow-50 border-yellow-300' : 'bg-white border-gray-200'}
                    `}
                    onClick={() => navigate(`/stories/${story.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="text-3xl">{story.icon}</div>
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-800">
                            {story.title}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Shared {formatRelativeTime(story.sharedAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      <span className={story.engagement.newComments > 0 ? 'text-orange-600 font-semibold' : 'text-gray-600'}>
                        💬 {story.engagement.commentCount}
                        {story.engagement.newComments > 0 && ` (+${story.engagement.newComments} new)`}
                      </span>
                      <span className={story.engagement.newReactions > 0 ? 'text-orange-600 font-semibold' : 'text-gray-600'}>
                        ❤️ {story.engagement.reactionCount}
                        {story.engagement.newReactions > 0 && ` (+${story.engagement.newReactions} new)`}
                      </span>
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-600">Shared with:</span>
                        <div className="flex -space-x-2">
                          {story.engagement.readBy.slice(0, 5).map(person => (
                            <div
                              key={person.id}
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white"
                              style={{ backgroundColor: person.avatarColor }}
                              title={person.firstName}
                            >
                              {person.firstName[0]}
                            </div>
                          ))}
                        </div>
                        <span className="text-xs text-gray-600">
                          {story.audience.length} people
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Actions */}
            <Card>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/tell-a-story')}
                  className="w-full bg-[#E65100] hover:bg-[#D84315] text-white px-4 py-4 rounded-xl font-bold flex items-center gap-3 transition-colors"
                >
                  <span className="text-2xl">🎙️</span>
                  <span>Record a Story</span>
                </button>
                <button
                  onClick={() => navigate('/photos')}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-3 rounded-xl font-semibold flex items-center gap-3 transition-colors"
                >
                  <span className="text-xl">📷</span>
                  <span>Upload Photos</span>
                </button>
                <button
                  onClick={() => navigate('/lifeevent/create')}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-3 rounded-xl font-semibold flex items-center gap-3 transition-colors"
                >
                  <span className="text-xl">🍳</span>
                  <span>Add Recipe</span>
                </button>
              </div>
            </Card>

            {/* Connection Summary */}
            <ConnectionSummary
              summary={{
                total_can_view: stats.adultCount + stats.minorCount,
                adults: stats.adultCount,
                minors: stats.minorCount,
                pending: 0,
                tree_only: 0
              }}
              onManageClick={() => navigate('/connections')}
              onAddMinorClick={() => setIsAddMinorModalOpen(true)}
            />

            {/* Recent Activity */}
            <Card>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Recent Activity
              </h3>
              <div className="space-y-3">
                {activities.slice(0, 4).map(activity => (
                  <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: activity.person.avatarColor }}
                    >
                      {activity.person.firstName[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800">
                        <span className="font-semibold">{activity.person.firstName}</span>
                        {activity.person.isMinor && activity.person.guardianName && (
                          <span className="text-xs text-blue-600"> (via {activity.person.guardianName})</span>
                        )}
                        {' '}
                        {activity.action === 'commented' && 'commented on'}
                        {activity.action === 'reacted' && 'loved'}
                        {activity.action === 'read' && 'read'}
                        {' '}
                        {activity.targetTitle && (
                          <span className="font-semibold">"{activity.targetTitle}"</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatRelativeTime(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Minor Modal */}
      <AddMinorModal
        isOpen={isAddMinorModalOpen}
        onClose={() => setIsAddMinorModalOpen(false)}
        onAdd={handleAddMinor}
        loading={isAddingMinor}
      />
    </div>
  );
}

ActiveState.propTypes = {
  data: PropTypes.object.isRequired
};

export default ActiveState;
